// История пункта (011: HIST-02; 014: состояние в поповере; 015: contracts/api.md, «ItemHistory»;
// research, Р4, Р9). Все принятые изменения отслеживаемых полей и положения пункта новыми сверху;
// значения — как хранятся (HTML примечания после санитайзера, текст срока, JSON ответственных
// и состояния), у ответственных и состояния — ещё текст для показа. Неделя вычисляется при чтении.
import { z } from '../kernel/zod.ts';
import { ItemAnswerChannelSchema, ItemAnswerKindSchema } from './answers.ts';
import { DateSchema, IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { ConflictFieldSchema } from './conflict.ts';

export const ITEM_HISTORY_PAGE = 100;

/** Виды событий истории пункта (`minutes.event_kind`, 011 и 012). */
export const itemEventKinds = [
  'progress_added',
  'progress_edited',
  'progress_archived',
  'progress_restored',
  'transferred',
  'edited',
  'created',
  'parent_resolved',
  'reverted',
  'moved',
  'archived',
  'restored',
  'split',
] as const;
export const ItemEventKindSchema = z.enum(itemEventKinds);
export type ItemEventKind = z.infer<typeof ItemEventKindSchema>;

/**
 * Группы фильтров панели «История пункта» (HIST-06): «Все» — без группы. 032 (FR-006,
 * ASG-HIST-07): «Ответственные» — кого назначали и снимали.
 */
export const historyGroups = ['due', 'contractor', 'note', 'state', 'assignees'] as const;
export const HistoryGroupSchema = z.enum(historyGroups);
export type HistoryGroup = z.infer<typeof HistoryGroupSchema>;

/**
 * Поля групп (data-model.md, «История пункта»); `title` — только в «Все»; 032 — `assignees`
 * в своей группе «Ответственные».
 * 021: «Фактическое окончание» — в сроках; правки прежнего «Подрядчика / факт» в историю не идут
 * (решение владельца 26.10.2026: прежнюю раскладку не поддерживаем).
 */
export const historyGroupFields = {
  due: ['dueRaw', 'dueTransfers', 'startRaw', 'actualEndRaw'],
  contractor: ['contractor'],
  note: ['noteHtml'],
  state: ['workState'],
  assignees: ['assignees'],
} as const satisfies Record<HistoryGroup, readonly string[]>;

/** Поля, правки которых попадают в историю пункта (остальные — оформление, скрытие, доп. поля). */
export const historyFields = [
  'title',
  ...historyGroupFields.assignees,
  ...historyGroupFields.due,
  ...historyGroupFields.contractor,
  ...historyGroupFields.note,
  ...historyGroupFields.state,
] as const;

/** Курсор страницы — «ревизия:номер события» последней записи предыдущей страницы. */
const HistoryCursorSchema = z
  .string()
  .regex(/^[1-9][0-9]{0,15}:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);

export const ItemHistoryParamsSchema = z.object({ id: IdSchema, itemId: IdSchema });
export const ItemHistoryQuerySchema = z.strictObject({
  group: HistoryGroupSchema.optional(),
  before: HistoryCursorSchema.optional(),
  limit: z.coerce.number().int().min(1).max(ITEM_HISTORY_PAGE).optional(),
  /** История зафиксированной недели (016: HIST-07): события до ревизии и времени фиксации. */
  snapshot: IdSchema.optional(),
});
export type ItemHistoryQuery = z.infer<typeof ItemHistoryQuerySchema>;

export const ItemHistoryEntrySchema = z.object({
  id: IdSchema,
  revision: z.number().int().min(1),
  kind: ItemEventKindSchema,
  /** Поле правки (kind = edited); у событий положения — null. */
  field: ConflictFieldSchema.nullable(),
  group: HistoryGroupSchema.nullable(),
  before: z.string().nullable(),
  after: z.string().nullable(),
  /** Текст для показа у ответственных (имена через «; ») и состояния (подпись и пояснение). */
  beforeDisplay: z.string().nullable(),
  afterDisplay: z.string().nullable(),
  actor: PersonRefSchema,
  at: TimestampSchema,
  /** Дата рабочей недели (research, Р4). */
  week: DateSchema,
  /** У пункта, созданного разделением: исходный пункт и его номер. */
  splitFrom: z.object({ itemId: IdSchema, number: z.string() }).nullable(),
  /** 029: правка из восстановления или возврата — подпись «Восстановлена версия от …». */
  version: z
    .object({
      kind: z.enum(['restore', 'revert']),
      revision: z.number().int().min(1),
      at: TimestampSchema,
    })
    .optional(),
  /** 030 (FR-022, FR-025): у правки срока из ответа — причина переноса. */
  reason: z.string().optional(),
  /** 030 (FR-025; анализ U2): правка — ответ из «Моих задач» этого вида. */
  answer: ItemAnswerKindSchema.optional(),
  /** 032 (FR-005, FR-009): откуда ответ; нет — из «Моих задач». */
  answerChannel: ItemAnswerChannelSchema.optional(),
});
export type ItemHistoryEntry = z.infer<typeof ItemHistoryEntrySchema>;

const HistoryCountSchema = z.number().int().min(0);

export const ItemHistorySourceSchema = z.object({
  sheetDate: DateSchema,
  sheet: z.string(),
  row: z.number().int().min(1),
  /** Исходные значения из строки Excel; отдельного подрядчика в файле нет — null. */
  cell: z.object({
    dueRaw: z.string(),
    contractor: z.string().nullable(),
    noteHtml: z.string(),
  }),
});
export type ItemHistorySource = z.infer<typeof ItemHistorySourceSchema>;

export const ItemHistoryResponseSchema = z.object({
  items: z.array(ItemHistoryEntrySchema).max(ITEM_HISTORY_PAGE),
  /** Курсор следующей страницы; null — записей больше нет. */
  next: HistoryCursorSchema.nullable(),
  /** Только в ответе без курсора. */
  counts: z
    .object({
      all: HistoryCountSchema,
      due: HistoryCountSchema,
      contractor: HistoryCountSchema,
      note: HistoryCountSchema,
      state: HistoryCountSchema,
      assignees: HistoryCountSchema,
    })
    .optional(),
  current: z.object({ dueRaw: z.string(), contractor: z.string() }).optional(),
  /** null — у пункта нет строки Excel (создан в системе). */
  source: ItemHistorySourceSchema.nullable().optional(),
  /** Граница истории снимка (016: WEEK-03) — при запросе с snapshot. */
  boundary: z
    .object({
      snapshotId: IdSchema,
      weekDate: DateSchema,
      revision: z.number().int().min(1),
      at: TimestampSchema,
    })
    .optional(),
});
export type ItemHistoryResponse = z.infer<typeof ItemHistoryResponseSchema>;
